import { useMemo, useState } from "react";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import {
  ROUTE_TYPE_BUCKETS,
  routeTypeBucket,
  routeTypeBucketLabel,
  routeTypeLabel,
  type RouteTypeBucket,
} from "../../lib/routeTypeDisplay";
import {
  ROUTE_AUTH_COLORS,
  ROUTE_AUTH_MODES,
  routeAuthLabel,
  type RouteAuthMode,
} from "../../lib/routeAuthDisplay";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { Button } from "../Button";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { OverflowBadges } from "../Settings/OverflowBadges";
import { PillBadge, SearchField } from "../Settings/shared";
import { RadarChart } from "../RadarChart";
import { type StatusSegment } from "../StatusBreakdownChart";
import { META_SEP } from "../MetaSeparator";
import { Text } from "../Text";
import { WorkflowBadge } from "../WorkflowBadge";
import {
  computeMonitorRoutes,
  countWorkflowsByRouteAuth,
  filterWorkflowSummaries,
  groupRoutesByWorkflow,
  matchingRoutes,
  type MonitorRouteRow,
  sortWorkflowRouteSummaries,
  type WorkflowRouteSort,
} from "./routeAuth";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
} from "./MonitoringDetailCard";
import { ListSection, NameBody, NameCell } from "./monitoringList";
import { useMonitorSpaceWorkflows } from "./useMonitorSpaceWorkflows";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

function parseRouteSort(value: string | undefined): WorkflowRouteSort {
  return value === "routes" ? "routes" : "name";
}

function routeLabel(route: MonitorRouteRow): string {
  const suffix = route.routeType
    ? `${META_SEP}${routeTypeLabel(route.routeType)}`
    : "";
  return `${route.route} (${routeAuthLabel(route.routeAuth)}${suffix})`;
}

export function RouteAuthPanel() {
  const { routeSort: routeSortParam } = useCurrentUrl(
    routes.monitoring.workflows
  );
  const routeSort = parseRouteSort(routeSortParam);
  const [authFilter, setAuthFilter] = useState<RouteAuthMode | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { workflows: monitorRoutes, result: monitorQuery } =
    useMonitorSpaceWorkflows();
  const displayName = useSpaceDisplayName();

  const workflowById = useMemo(() => {
    const map = new Map<string, (typeof monitorRoutes)[number]>();
    for (const workflow of monitorRoutes) {
      map.set(workflow.id, workflow);
    }
    return map;
  }, [monitorRoutes]);

  const spaceById = useMemo(() => {
    const map = new Map<string, DisplayableSpace>();
    for (const workflow of monitorRoutes) {
      map.set(workflow.spaceId, workflow.space);
    }
    return map;
  }, [monitorRoutes]);

  const allRows = useMemo(
    () => computeMonitorRoutes(monitorRoutes),
    [monitorRoutes]
  );

  const workflowSummaries = useMemo(
    () => groupRoutesByWorkflow(allRows),
    [allRows]
  );

  const authCounts = useMemo(
    () => countWorkflowsByRouteAuth(workflowSummaries),
    [workflowSummaries]
  );

  const authSegments = useMemo<StatusSegment[]>(() => {
    // Route-type breakdown per auth mode, surfaced in the radar's hover tooltip.
    const typeByAuth = new Map<
      RouteAuthMode,
      Record<RouteTypeBucket, number>
    >();
    for (const mode of ROUTE_AUTH_MODES) {
      typeByAuth.set(
        mode,
        Object.fromEntries(ROUTE_TYPE_BUCKETS.map((b) => [b, 0])) as Record<
          RouteTypeBucket,
          number
        >
      );
    }
    for (const row of allRows) {
      typeByAuth.get(row.routeAuth)![routeTypeBucket(row.routeType)] += 1;
    }
    // Every auth mode is an axis so the radar keeps a stable shape.
    return ROUTE_AUTH_MODES.map((mode) => {
      const counts = typeByAuth.get(mode)!;
      return {
        key: mode,
        count: authCounts[mode],
        color: ROUTE_AUTH_COLORS[mode],
        label: routeAuthLabel(mode),
        note:
          mode === "public"
            ? "Public links can be triggered by anyone with the URL — there’s no authentication. Treat them as exposed to the open internet and review them carefully."
            : undefined,
        details: ROUTE_TYPE_BUCKETS.filter((b) => counts[b] > 0).map((b) => ({
          label: routeTypeBucketLabel(b),
          value: String(counts[b]),
        })),
      };
    });
  }, [authCounts, allRows]);

  const filteredSummaries = useMemo(
    () => filterWorkflowSummaries(workflowSummaries, authFilter),
    [workflowSummaries, authFilter]
  );

  const sortedSummaries = useMemo(
    () => sortWorkflowRouteSummaries(filteredSummaries, routeSort, authFilter),
    [filteredSummaries, routeSort, authFilter]
  );

  const displayedSummaries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedSummaries;
    return sortedSummaries.filter((summary) =>
      summary.workflowName.toLowerCase().includes(q)
    );
  }, [sortedSummaries, search]);

  const setRouteSort = (next: WorkflowRouteSort) => {
    navigate(
      routes.monitoring.workflows,
      { routeSort: next },
      { replace: true }
    );
  };

  if (monitorQuery.type !== "complete") {
    return <PanelSkeleton />;
  }

  return (
    <Panel>
      <PanelHeader>
        <Text variant="text-small-md">Workflow routes</Text>
        <TotalRow>
          <Text variant="display-md">{workflowSummaries.length}</Text>
        </TotalRow>
      </PanelHeader>

      {workflowSummaries.length > 0 ? (
        <RadarChart
          segments={authSegments}
          onSegmentClick={(s) => {
            setAuthFilter(s.key as RouteAuthMode);
            setSearch("");
            setOpen(true);
          }}
        />
      ) : (
        <RadarChart segments={authSegments} placeholder />
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        style={DETAIL_MODAL_STYLE}
      >
        <ModalHeader>
          <ModalTitle icon="link-2" title="Workflow routes" />
          <ModalHeaderRight>
            <Button
              size="l"
              variant="secondary"
              icon="x"
              onClick={() => setOpen(false)}
            />
          </ModalHeaderRight>
        </ModalHeader>

        <ModalBody style={{ gap: 0 }}>
          <ListSection
            title={
              authFilter ? `${routeAuthLabel(authFilter)} routes` : "Routes"
            }
            description="The workflows exposing these routes, with each one’s matching routes."
            action={
              <SearchField
                placeholder="Search workflows…"
                value={search}
                onChange={setSearch}
                width={280}
                autoFocus
              />
            }
          >
            <ListTable
              headers={{
                name: "Workflow",
                count: "Routes",
                matching: "Matching routes",
              }}
              columns={{ name: "flex", count: "content", matching: "flex" }}
              sortableColumns={["name", "count"]}
              sortColumn={routeSort === "routes" ? "count" : "name"}
              sortDirection={routeSort === "name" ? "asc" : "desc"}
              onSort={(col) =>
                setRouteSort(col === "count" ? "routes" : "name")
              }
              emptyState={
                <Text variant="text-regular" color="var(--text-secondary)">
                  {search
                    ? "No workflows match your search."
                    : "No workflows match this auth type and filter."}
                </Text>
              }
              items={displayedSummaries.map((summary) => {
                const workflow = workflowById.get(summary.workflowId);
                const space = spaceById.get(summary.spaceId);
                const matching = matchingRoutes(summary, authFilter);
                return {
                  key: summary.workflowId,
                  link: makeUrl(routes.space.workflow, {
                    spaceId: summary.spaceId,
                    workflowId: summary.workflowId,
                  }),
                  name: (hover: boolean) => (
                    <NameCell>
                      {workflow ? (
                        <WorkflowBadge
                          workflow={{
                            id: workflow.id,
                            badgeSeed: workflow.badgeSeed,
                            pausedAt: workflow.pausedAt,
                            branches: workflow.branches
                              ? [workflow.branches]
                              : undefined,
                          }}
                          hover={hover}
                        />
                      ) : null}
                      <NameBody>
                        <Text variant="text-regular-md" ellipsis>
                          {summary.workflowName}
                        </Text>
                        {space ? (
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                            ellipsis
                          >
                            {displayName(space)}
                          </Text>
                        ) : null}
                      </NameBody>
                    </NameCell>
                  ),
                  count: <Text variant="text-small">{matching.length}</Text>,
                  matching: (
                    <OverflowBadges
                      items={matching}
                      keyFn={(route) => route.route}
                      renderBadge={(route) => (
                        <PillBadge>{routeLabel(route)}</PillBadge>
                      )}
                      overflowTooltip={(hidden) =>
                        hidden.map(routeLabel).join(", ")
                      }
                    />
                  ),
                };
              })}
            />
          </ListSection>
        </ModalBody>

        <ModalFooter>
          <Text variant="text-small" color="var(--text-secondary)">
            {sortedSummaries.length}{" "}
            {sortedSummaries.length === 1 ? "workflow" : "workflows"}
          </Text>
          <Button size="l" variant="primary" onClick={() => setOpen(false)}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </Panel>
  );
}
